import Animated, {
  useAnimatedStyle,
  withTiming,
} from 'react-native-reanimated';
import React, { CSSProperties, useState } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { DEFAULT_EASING_IN_OUT_CONFIG } from '@experiences/blocs/utils/animations';
import { DraggableProvided } from '@hello-pangea/dnd';
import { Icons, XStack } from '@usepepper/sprout-kit-ui';
import { StyleProp, View, ViewStyle } from 'react-native';

interface Props {
  content: React.ReactNode;
  dataIndex?: number;
  disabled: boolean;
  dragHandleAccessibilityLabel?: string;
  dragHandleAnchorHeight?: number;
  isDragging: boolean;
  measureRef?: (node: HTMLElement | null) => void;
  /** 1-based position within the full list — conveys order to screen readers. */
  posInSet?: number;
  provided: DraggableProvided;
  rowStyle?: StyleProp<ViewStyle>;
  /** Total row count — lets screen readers announce position in a virtual list. */
  setSize?: number;
  showHandleOnHover: boolean;
  wrapperStyle?: CSSProperties;
}

const DRAG_PREVIEW_OPACITY = 0.5;
// @hello-pangea/dnd needs a real div for its drag-handle props; this keeps that
// div shrink-wrapped to the handle button.
const HANDLE_DOM_STYLE: CSSProperties = { display: 'flex' };

export const SortableListItem: React.FC<Props> = ({
  content,
  dataIndex,
  disabled,
  dragHandleAccessibilityLabel,
  dragHandleAnchorHeight,
  isDragging,
  measureRef,
  posInSet,
  provided,
  rowStyle,
  setSize,
  showHandleOnHover,
  wrapperStyle,
}) => {
  const [styles, theme] = useStyles();
  const [isRowHovered, setIsRowHovered] = useState(false);
  const [isHandleFocused, setIsHandleFocused] = useState(false);

  // `disabled` rows render no handle (below), so this only gates the reveal.
  const isHandleVisible =
    !showHandleOnHover || isRowHovered || isDragging || isHandleFocused;
  const revealStyle = useAnimatedStyle(() => ({
    opacity: withTiming(isHandleVisible ? 1 : 0, DEFAULT_EASING_IN_OUT_CONFIG),
  }));

  const overlayStyle: ViewStyle =
    dragHandleAnchorHeight !== undefined
      ? { height: dragHandleAnchorHeight }
      : { bottom: 0 };

  const handle = disabled ? null : (
    <Animated.View style={[styles.handleOverlay, overlayStyle, revealStyle]}>
      <div
        {...provided.dragHandleProps}
        aria-label={dragHandleAccessibilityLabel}
        onBlurCapture={() => setIsHandleFocused(false)}
        onFocusCapture={() => setIsHandleFocused(true)}
        role="button"
        style={HANDLE_DOM_STYLE}
      >
        <XStack
          alignItems="center"
          borderRadius={theme.sk.radii.radiusSmall}
          hoverStyle={{ backgroundColor: theme.sk.color.bg.secondary.hover }}
          justifyContent="center"
          padding={theme.sk.spacing.spaceXsmall}
        >
          <Icons.GripVertical
            color={theme.sk.color.icon.secondary}
            size={theme.sk.spacing.spaceMedium}
          />
        </XStack>
      </div>
    </Animated.View>
  );

  const contentNode = <View style={styles.content}>{content}</View>;

  return (
    // @hello-pangea/dnd requires a div for its draggable props; the same node is
    // the virtualizer's measured element so dynamic (expanded) heights are read.
    <div
      aria-posinset={posInSet}
      aria-setsize={setSize}
      // The virtualizer reads data-index off this node to map its measured height
      data-index={dataIndex}
      ref={(node) => {
        provided.innerRef(node);
        measureRef?.(node);
      }}
      role="listitem"
      {...provided.draggableProps}
      style={{ ...wrapperStyle, ...provided.draggableProps.style }}
    >
      <View style={rowStyle}>
        <XStack
          alignItems="center"
          backgroundColor={
            isDragging ? 'transparent' : theme.sk.color.bg.primary.default
          }
          // tamagui drops onHoverIn/Out unless the row also has a hoverStyle; use
          // raw DOM mouse events (web-only file) so the reveal actually fires.
          onMouseEnter={() => setIsRowHovered(true)}
          onMouseLeave={() => setIsRowHovered(false)}
          opacity={isDragging ? DRAG_PREVIEW_OPACITY : 1}
          position="relative"
        >
          {contentNode}
          {handle}
        </XStack>
      </View>
    </div>
  );
};

const useStyles = createStyles(() => ({
  content: {
    flex: 1,
    userSelect: 'none',
  },
  handleOverlay: {
    justifyContent: 'center',
    position: 'absolute',
    right: 0,
    top: 0,
  },
}));
